import momentTZ from "moment-timezone";
import { useIntl } from "react-intl";

import { ReactComponent as CargoShipIcon } from "assets/images/iconsV2/cargo-ship.svg";
import { ReactComponent as PlaneIcon } from "assets/images/iconsV2/plane.svg";
import Badge from "components/v2/Badge/Badge";
import { classNames } from "helpers/commonHelpers";

const EMPTY = "-";

const formatStopDatetime = ({ datetime, timezone }: { datetime?: string; timezone?: string }) => {
  if (!datetime || !timezone) return null;
  return momentTZ(datetime).tz(timezone).format("DD MMM YYYY, HH:mm (UTC Z)");
};

const getStopTimeText = (stop: Record<string, unknown> | undefined, intl: ReturnType<typeof useIntl>) => {
  if (!stop) return null;

  const stopTimeType = stop.stop_time_type as string | undefined;
  const afterDateTime = formatStopDatetime({
    datetime: stop.stop_after_datetime as string | undefined,
    timezone: stop.stop_timezone as string | undefined,
  });
  const beforeDateTime = formatStopDatetime({
    datetime: stop.stop_before_datetime as string | undefined,
    timezone: stop.stop_timezone as string | undefined,
  });
  const capitalize = (text = "") => (text ? `${text.charAt(0).toUpperCase()}${text.slice(1)}` : "");

  if (stopTimeType === "After") {
    if (!afterDateTime) return null;
    return `${capitalize(intl.formatMessage({ id: "general.after", defaultMessage: "After" }))} ${afterDateTime}`;
  }
  if (stopTimeType === "Before") {
    if (!beforeDateTime) return null;
    return `${capitalize(intl.formatMessage({ id: "general.before", defaultMessage: "Before" }))} ${beforeDateTime}`;
  }
  if (stopTimeType === "Between") {
    if (!afterDateTime || !beforeDateTime) return null;
    return `${afterDateTime} - ${beforeDateTime}`;
  }
  if (stopTimeType === "ASAP" || stopTimeType === "ASAP Delivery") {
    return intl.formatMessage({ id: "quote-request-detail.asap", defaultMessage: "ASAP" });
  }

  return afterDateTime || beforeDateTime || null;
};

const getStopPrimaryDatetime = (stop?: Record<string, unknown>) =>
  formatStopDatetime({
    datetime: stop?.stop_after_datetime as string | undefined,
    timezone: stop?.stop_timezone as string | undefined,
  }) ||
  formatStopDatetime({
    datetime: stop?.stop_before_datetime as string | undefined,
    timezone: stop?.stop_timezone as string | undefined,
  });

const getAddressDetails = (stop?: Record<string, unknown>) =>
  stop?.address_details as Record<string, unknown> | undefined;

const getStopStreet = (stop?: Record<string, unknown>) => {
  const addressDetails = getAddressDetails(stop);
  const streetAddress = addressDetails?.street_address as string | undefined;
  const streetAddressLineTwo = addressDetails?.street_address_line_two as string | undefined;
  if (!streetAddress && !streetAddressLineTwo) return null;
  return [streetAddress, streetAddressLineTwo].filter(Boolean).join(", ");
};

const getStopAirport = (stop?: Record<string, unknown>) => {
  if (!stop || stop.stop_address_type !== "Airport") return null;
  const airportDetails = stop.airport_details as Record<string, unknown> | undefined;
  const airportName = (airportDetails?.name as string) || (getAddressDetails(stop)?.city as string) || "";
  const airportCode = (airportDetails?.code as string) || "";
  if (!airportName && !airportCode) return null;
  if (!airportCode) return airportName;
  if (!airportName) return airportCode;
  return `${airportName} (${airportCode})`;
};

const getStopSeaport = (stop?: Record<string, unknown>) => {
  if (!stop || stop.stop_address_type !== "Port") return null;
  const seaportDetails = stop.seaport_details as Record<string, unknown> | undefined;
  const seaportName = (seaportDetails?.name as string) || "";
  const seaportCode = (seaportDetails?.un_locode as string) || "";
  if (!seaportName && !seaportCode) return null;
  if (!seaportCode) return seaportName;
  if (!seaportName) return seaportCode;
  return `${seaportName} (${seaportCode})`;
};

const isDuplicateOfPortLabel = (value: string, portLabel: string) => {
  const a = value.trim().toLowerCase();
  const b = portLabel.trim().toLowerCase();
  if (!a || !b) return false;
  return a === b || a.includes(b) || b.includes(a);
};

export const toAddressPart = (value: unknown): string | undefined => {
  if (typeof value !== "string") return undefined;
  const trimmed = value.trim();
  return trimmed || undefined;
};

const getPackingStopAddressLines = (stop?: Record<string, unknown>) => {
  const airport = getStopAirport(stop);
  const seaport = getStopSeaport(stop);
  const portLabel = airport || seaport || "";
  const street = getStopStreet(stop);
  const addressDetails = getAddressDetails(stop);
  const regionParts = [
    toAddressPart(addressDetails?.city),
    toAddressPart(addressDetails?.state),
    toAddressPart(addressDetails?.zip_code) || toAddressPart(addressDetails?.postal_code),
    toAddressPart(addressDetails?.country),
  ].filter((part): part is string => Boolean(part) && (!portLabel || !isDuplicateOfPortLabel(part, portLabel)));
  const addressName =
    toAddressPart(addressDetails?.name) || toAddressPart(stop?.address_name);
  const isDuplicateName =
    !!addressName &&
    ((!!portLabel && isDuplicateOfPortLabel(addressName, portLabel)) ||
      (!!street && isDuplicateOfPortLabel(addressName, street)));

  return {
    airport,
    seaport,
    addressName: addressName && !isDuplicateName ? addressName : null,
    street: street && (!portLabel || !isDuplicateOfPortLabel(street, portLabel)) ? street : null,
    regionLine: regionParts.join(", ") || null,
  };
};

function PortTypeBadge({ addressType, portLabel }: { addressType?: string; portLabel?: string }) {
  const intl = useIntl();
  const isAirport = addressType === "Airport";
  const isSeaport = addressType === "Port";
  if ((!isAirport && !isSeaport) || !portLabel) return null;

  return (
    <div className={classNames("rfv2-review__stop-address-type", "rfv2-review__stop-address-type--stacked")}>
      <Badge
        label={
          isAirport
            ? intl.formatMessage({ id: "request-form.review.schedule.airport", defaultMessage: "Airport" })
            : intl.formatMessage({ id: "request-form.review.schedule.port", defaultMessage: "Port" })
        }
        color="gray"
        type="status-badge"
        variant="icon-leading"
        size="sm"
        icon={isAirport ? <PlaneIcon width={12} height={12} /> : <CargoShipIcon width={12} height={12} />}
      />
      <span className="rfv2-review__field-value">{portLabel}</span>
    </div>
  );
}

export function StopCell({ stop, intl }: { stop?: Record<string, unknown>; intl: ReturnType<typeof useIntl> }) {
  if (!stop) return <span>{EMPTY}</span>;

  const primary = getStopPrimaryDatetime(stop);
  const requested = getStopTimeText(stop, intl);
  const { airport, seaport, addressName, street, regionLine } = getPackingStopAddressLines(stop);

  if (!primary && !requested && !airport && !seaport && !addressName && !street && !regionLine) {
    return <span>{EMPTY}</span>;
  }

  return (
    <div className="rfv2-review-packing-list__stop-cell">
      {primary && <span className="rfv2-review-packing-list__stop-primary">{primary}</span>}
      {requested && (
        <span className="shipment-details-sheet__packing-stop-requested">
          {intl.formatMessage({ id: "request-form.review.packingList.requested", defaultMessage: "Requested" })}:{" "}
          {requested}
        </span>
      )}
      <div className="rfv2-review-packing-list__stop-address">
        {airport && <PortTypeBadge addressType="Airport" portLabel={airport} />}
        {seaport && <PortTypeBadge addressType="Port" portLabel={seaport} />}
        {addressName && <span className="rfv2-review__field-value">{addressName}</span>}
        {street && <span className="rfv2-review__field-value">{street}</span>}
        {regionLine && <span className="rfv2-review__field-value">{regionLine}</span>}
      </div>
    </div>
  );
}
